<template>
  <label
    class="jps-checkbox"
    ref="label"
    :disabled="disabled"
    @keydown.prevent.enter="click"
  >
    <input
      v-model="valueInner"
      type="checkbox"
      :indeterminate="indeterminate"
      ref="input"
      :disabled="disabled"
      :required="required"
    />
    <span class="check-mark"></span>
    <span class="jps-checkbox-label">
      <slot/>
    </span>
  </label>
</template>

<script lang="ts" setup>
interface Props {
  modelValue?: boolean
  required?: boolean
  nativeValue?: string
  disabled?: boolean
  indeterminate?: boolean
}
const props = withDefaults(defineProps<Props>(), {
  required: false,
  nativeValue: null,
  disabled: false,
  indeterminate: false,
})

const emit = defineEmits(['update:modelValue'])

const label = ref<HTMLElement>()

const click = () => {
  label.value.click()
}

const valueInner = computed({
  get: () => {
    return props.modelValue
  },
  set: (v) => {
    emit('update:modelValue', v)
  },
})
</script>

<style lang="scss" scoped>
@use '@/assets/styles/scss/vue' as *;

$checkbox-active-background-color: $jps-key-blue !default;
$checkbox-background-color: transparent !default;
$checkbox-border-color: $jps-gray-500 !default;
$checkbox-border-radius: 4px !default;
$checkbox-border-width: 2px !default;
$checkbox-checkmark-color: $white;
$checkbox-size: 1.25rem !default;

.jps-checkbox {
  display: inline-flex;
  align-items: center;
  & + .jps-checkbox {
    margin-left: 0.5em;
  }
  > input[type='checkbox'] {
    position: absolute;
    left: 0;
    opacity: 0;
    z-index: -1;
    + .check-mark {
      cursor: pointer;
      width: 1.25rem;
      height: 1.25rem;
      flex-shrink: 0;
      border-radius: 4px;
      border-width: 2px;
      border-color: $jps-gray-500;
      border-style: solid;
      transition: all 250ms cubic-bezier(0.64, 0.09, 0.08, 1);
      background: transparent;
    }
    &:checked + .check-mark {
      background: $checkbox-active-background-color
        url(checkmark($checkbox-checkmark-color)) no-repeat center center;
      border-color: $checkbox-active-background-color;
    }
    &:indeterminate + .check-mark {
      background: $checkbox-active-background-color
        url(indeterminate($checkbox-checkmark-color)) no-repeat center center;
      border-color: $checkbox-active-background-color;
    }
    &:focus {
      + .check-mark {
        box-shadow: 0 0 0.5em rgba($jps-gray-500, 0.8);
      }
      &:checked + .check-mark {
        box-shadow: 0 0 0.5em rgba($checkbox-active-background-color, 0.8);
      }
    }

  }
  .jps-checkbox-label {
    padding-left: 0.5rem;
    cursor: pointer;
  }
  &:disabled {
    opacity: 0.5;
    cursor: normal;
    pointer-events: none;
  }
  &:hover {
    input[type='checkbox']:not(:disabled) + .check-mark {
      border-color: $checkbox-active-background-color;
    }
  }
}
</style>
